import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, HStack, Spinner, Text, VStack } from '../../../components/ui';
import { Checkbox } from '../../../components/ui/checkbox';
import {
    DialogActionTrigger, DialogBackdrop, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogPositioner, DialogRoot, DialogTitle
} from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { Select } from '../../../components/ui/select';
import { CreateFeatureFlagRequest, featureFlagService } from '../../../services/featureFlagService';
import { ServerConfig, serverService } from '../../../services/serverService';
import { ToolConfig, toolService } from '../../../services/toolService';
import { getThemeColor } from '../../../theme/colors';
import { showToast } from '../../../utils/toast';

interface CreateFeatureFlagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const KEY_REGEX = /^[a-z0-9_]+$/;

const CreateFeatureFlagModal: React.FC<CreateFeatureFlagModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateFeatureFlagRequest>({
    name: '',
    key: '',
    description: '',
    category: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [servers, setServers] = useState<ServerConfig[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [tools, setTools] = useState<ToolConfig[]>([]);
  const [selectedToolIds, setSelectedToolIds] = useState<Set<string>>(new Set());
  const [loadingServers, setLoadingServers] = useState(false);
  const [loadingTools, setLoadingTools] = useState(false);

  // Load servers on mount
  useEffect(() => {
    const loadServers = async () => {
      try {
        setLoadingServers(true);
        const data = await serverService.getAllServers();
        setServers(data.filter(s => s.isActive));
      } catch (error: any) {
        showToast({
          title: 'Error loading servers',
          description: error.message,
          status: 'error'
        });
      } finally {
        setLoadingServers(false);
      }
    };
    
    if (isOpen) {
      loadServers();
    }
  }, [isOpen]);

  // Load tools when server is selected
  useEffect(() => {
    const loadTools = async () => {
      if (!selectedServerId) {
        setTools([]);
        setSelectedToolIds(new Set());
        return;
      }

      try {
        setLoadingTools(true);
        // Clear selected tools when switching servers
        setSelectedToolIds(new Set());
        const data = await toolService.getServerTools(selectedServerId);
        setTools(data.filter(t => t.isActive));
      } catch (error: any) {
        showToast({
          title: t('app.featureFlags.createModal.errorLoadingTools'),
          description: error.message,
          status: 'error'
        });
      } finally {
        setLoadingTools(false);
      }
    };

    loadTools();
  }, [selectedServerId]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('app.featureFlags.createModal.nameRequired');
    }

    if (!formData.key.trim()) {
      newErrors.key = t('app.featureFlags.createModal.keyRequired');
    } else if (!KEY_REGEX.test(formData.key)) {
      newErrors.key = t('app.featureFlags.createModal.keyInvalid');
    }

    if (!selectedServerId) {
      newErrors.server = t('app.featureFlags.createModal.serverRequired');
    }

    if (selectedServerId && selectedToolIds.size === 0) {
      newErrors.tools = t('app.featureFlags.createModal.toolsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const createdFlag = await featureFlagService.createFlag(formData);
      
      // Assign tools to the flag (now mandatory)
      await featureFlagService.assignToolsToFlag(
        createdFlag.key,
        selectedServerId,
        Array.from(selectedToolIds)
      );
      
      showToast({
        title: t('app.featureFlags.createModal.createSuccess'),
        description: `Access policy "${formData.name}" has been created with ${selectedToolIds.size} tool(s)`,
        status: 'success'
      });
      onSuccess();
      handleClose();
    } catch (error: any) {
      showToast({
        title: t('app.featureFlags.createModal.createError'),
        description: error.response?.data?.message || error.message,
        status: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', key: '', description: '', category: '' });
    setErrors({});
    setSelectedServerId('');
    setSelectedToolIds(new Set());
    setTools([]);
    onClose();
  };

  const handleKeyChange = (value: string) => {
    // Auto-format: convert to lowercase, replace spaces with underscores
    const formatted = value.toLowerCase().replace(/\s+/g, '_');
    setFormData({ ...formData, key: formatted });
  };

  const handleToolToggle = (toolId: string, checked: boolean) => {
    setSelectedToolIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(toolId);
      } else {
        newSet.delete(toolId);
      }
      return newSet;
    });
  };

  const handleSelectAllTools = () => {
    setSelectedToolIds(new Set(tools.map(t => t.id)));
  };

  const handleDeselectAllTools = () => {
    setSelectedToolIds(new Set());
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={(e: { open: boolean }) => e.open ? undefined : handleClose()} size="xl" placement="center">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="800px" maxH="90vh" display="flex" flexDirection="column">
        <DialogHeader position="sticky" top={0} bg="white" zIndex={1} borderBottomWidth="1px" borderColor="gray.200" _dark={{ bg: "gray.800", borderColor: "gray.700" }}>
          <DialogTitle>{t('app.featureFlags.createModal.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody pb={6} overflowY="auto" flex={1}>
          <VStack gap={4} align="stretch">
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <Field label={t('app.featureFlags.createModal.nameLabel')} invalid={!!errors.name} errorText={errors.name} required>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('app.featureFlags.createModal.namePlaceholder')}
                />
              </Field>

              <Field 
                label={t('app.featureFlags.createModal.keyLabel')} 
                invalid={!!errors.key} 
                errorText={errors.key}
                helperText={t('app.featureFlags.createModal.keyHelper')}
                required
              >
                <Input
                  value={formData.key}
                  onChange={(e) => handleKeyChange(e.target.value)}
                  placeholder={t('app.featureFlags.createModal.keyPlaceholder')}
                  fontFamily="mono"
                />
              </Field>
            </Grid>

            <Field label={t('app.featureFlags.createModal.descriptionLabel')}>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('app.featureFlags.createModal.descriptionPlaceholder')}
              />
            </Field>

            {/* Server Selection */}
            <Select
              label={t('app.featureFlags.createModal.serverLabel')}
              helperText={t('app.featureFlags.createModal.serverHelper')}
              placeholder={loadingServers ? t('app.featureFlags.createModal.loadingServers') : t('app.featureFlags.createModal.serverPlaceholder')}
              items={servers.map(s => ({ label: s.name, value: s.id! }))}
              value={selectedServerId ? [selectedServerId] : []}
              onValueChange={(e: any) => setSelectedServerId(e.value[0] || '')}
              disabled={loadingServers}
              required
              errorText={errors.server}
            />

            {/* Tool Selection */}
            {selectedServerId && (
              <Field 
                label={t('app.featureFlags.createModal.toolsLabel')} 
                required
              >
                {loadingTools ? (
                  <Box p={4} textAlign="center">
                    <Spinner size="sm" mr={2} />
                    <Text display="inline" fontSize="sm" color="gray.600">{t('app.featureFlags.createModal.loadingTools')}</Text>
                  </Box>
                ) : tools.length === 0 ? (
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <Text fontSize="sm" color="gray.600">{t('app.featureFlags.createModal.noTools')}</Text>
                  </Box>
                ) : (
                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" color="gray.600">
                        {t('app.featureFlags.createModal.toolsSelected', { selected: selectedToolIds.size, total: tools.length })}
                      </Text>
                      <HStack gap={2}>
                        <Button size="xs" variant="ghost" onClick={handleSelectAllTools}>
                          {t('app.featureFlags.createModal.selectAll')}
                        </Button>
                        <Button size="xs" variant="ghost" onClick={handleDeselectAllTools}>
                          {t('app.featureFlags.createModal.deselectAll')}
                        </Button>
                      </HStack>
                    </HStack>
                    <Box 
                      maxH="300px" 
                      overflowY="auto" 
                      borderRadius="md" 
                      p={3}
                      bg="gray.50"
                      _dark={{ bg: "gray.900", borderColor: "gray.700" }}
                    >
                      <VStack align="stretch" gap={2}>
                        {tools.map((tool) => (
                          <Box
                            key={tool.id}
                            p={2}
                            bg="white"
                            borderRadius="md"
                            borderColor={selectedToolIds.has(tool.id) ? 'blue.200' : 'gray.200'}
                            _dark={{ bg: "gray.800", borderColor: selectedToolIds.has(tool.id) ? 'blue.500' : 'gray.700' }}
                            cursor="pointer"
                            onClick={() => handleToolToggle(tool.id, !selectedToolIds.has(tool.id))}
                            _hover={{ borderColor: selectedToolIds.has(tool.id) ? 'blue.300' : 'gray.300' }}
                          >
                            <Checkbox
                              checked={selectedToolIds.has(tool.id)}
                              onCheckedChange={(e: any) => {
                                e.stopPropagation?.();
                                handleToolToggle(tool.id, !!e.checked);
                              }}
                              pointerEvents="none"
                            >
                              <VStack align="start" gap={0}>
                                <Text fontWeight="medium" fontSize="sm">{tool.name}</Text>
                                {tool.description && (
                                  <Text fontSize="xs" color="gray.600" _dark={{ color: "gray.400" }}>{tool.description}</Text>
                                )}
                              </VStack>
                            </Checkbox>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                    {errors.tools && selectedToolIds.size === 0 && (
                      <Text color="red.500" fontSize="sm" mt={2}>
                        {errors.tools}
                      </Text>
                    )}
                  </Box>
                )}
              </Field>
            )}

            <Box p={3} bg={getThemeColor('alert.info.bg')} borderRadius="md" borderColor={getThemeColor('alert.info.border')} borderWidth="1px">
              <Text fontSize="sm" color={getThemeColor('alert.info.text')}>
                <strong>{t('app.featureFlags.createModal.tipTitle')}</strong> {t('app.featureFlags.createModal.tipMessage')}
              </Text>
            </Box>
          </VStack>
        </DialogBody>
        <DialogFooter position="sticky" bottom={0} bg="white" zIndex={1} borderTopWidth="1px" borderColor="gray.200" _dark={{ bg: "gray.800", borderColor: "gray.700" }}>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleClose}>
              {t('app.common.cancel')}
            </Button>
          </DialogActionTrigger>
          <Button
            colorPalette="blue"
            onClick={handleSubmit}
            loading={loading}
          >
            {t('app.featureFlags.createModal.createButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};

export default CreateFeatureFlagModal;
